export type PillarKey = 'water' | 'food' | 'power' | 'skills' | 'community'

export interface QuizAnswer {
  label: string
  /** 0 (unprepared or unknown) to 3 (covered). */
  score: 0 | 1 | 2 | 3
}

export interface QuizQuestion {
  pillar: PillarKey
  text: string
  answers: QuizAnswer[]
}

export interface Pillar {
  key: PillarKey
  label: string
  /** One-line action for when this pillar is among the weakest. */
  action: string
  /** Dispatch that covers the action, if one exists. */
  href?: string
  hrefTitle?: string
}

// The Readiness Score, both stages, runs entirely in the browser: one
// question per pillar, each answer carries a 0-3 score, and the Stage 2
// report is computed from those scores (see scoreQuiz below). No email, no
// server, no stored data. It is a deliberately simple model, and the copy
// around it should never claim more precision than five questions can give.
export const quizQuestions: QuizQuestion[] = [
  {
    pillar: 'water',
    text: 'If the tap ran dry today, how long could your household drink?',
    answers: [
      { label: 'Under 3 days', score: 1 },
      { label: 'About a week', score: 2 },
      { label: 'Two weeks+', score: 3 },
      { label: 'Not sure', score: 0 },
    ],
  },
  {
    pillar: 'food',
    text: 'How many days of food could you eat without a store run?',
    answers: [
      { label: 'A few days', score: 1 },
      { label: '1–2 weeks', score: 2 },
      { label: 'A month+', score: 3 },
      { label: 'No idea', score: 0 },
    ],
  },
  {
    pillar: 'power',
    text: 'If the power dropped for 3 days, what happens?',
    answers: [
      { label: "We're stuck", score: 0 },
      { label: 'Some backup', score: 2 },
      { label: 'Fully covered', score: 3 },
      { label: 'Untested', score: 1 },
    ],
  },
  {
    pillar: 'skills',
    text: 'Could you navigate home with no phone?',
    answers: [
      { label: 'No', score: 0 },
      { label: 'Maybe', score: 1 },
      { label: 'Yes', score: 3 },
      { label: 'Never tried', score: 1 },
    ],
  },
  {
    pillar: 'community',
    text: 'Do your neighbors have any shared plan?',
    answers: [
      { label: 'None', score: 0 },
      { label: 'Loose one', score: 2 },
      { label: 'Solid one', score: 3 },
      { label: 'Unsure', score: 1 },
    ],
  },
]

export const pillars: Pillar[] = [
  {
    key: 'water',
    label: 'Water',
    action: 'Store a gallon per person per day for two weeks, in containers that hold up, and rotate it.',
    href: '/articles/water-storage-mistake',
    hrefTitle: 'The Water Storage Mistake Almost Everyone Makes',
  },
  {
    key: 'food',
    label: 'Food',
    action: 'Build two weeks of food you already eat, then extend it with sealed long-term staples.',
    href: '/articles/how-long-does-stored-rice-actually-last',
    hrefTitle: 'How Long Does Stored Rice Actually Last?',
  },
  {
    key: 'power',
    label: 'Home & Power',
    action: 'Decide what genuinely has to run in an outage, then size a backup for that and nothing more.',
    href: '/articles/what-size-generator-do-i-need',
    hrefTitle: 'What Size Generator Do I Need?',
  },
  {
    key: 'skills',
    label: 'Skills',
    action: 'Learn to take a bearing and read contour lines on a paper map before the day you need to.',
    href: '/articles/reading-a-compass',
    hrefTitle: 'Reading a Compass When Your Phone Dies',
  },
  {
    key: 'community',
    label: 'Community',
    action: 'Talk to two neighbors this week about who has what, and who checks on whom when something happens.',
  },
]

export interface PillarResult extends Pillar {
  score: number
  pct: number
}

export interface QuizResult {
  band: string
  headline: string
  total: number
  max: number
  pillars: PillarResult[]
  gaps: PillarResult[]
}

const bands: { min: number; band: string; headline: string }[] = [
  { min: 13, band: 'A', headline: 'Well Prepared' },
  { min: 10, band: 'B', headline: 'Solid Baseline, With Gaps' },
  { min: 7, band: 'C', headline: 'Real Gaps To Close' },
  { min: 0, band: 'D', headline: 'Start This Week' },
]

/** answers[i] is the chosen answer index for quizQuestions[i]. */
export function scoreQuiz(answers: number[]): QuizResult {
  const scored: PillarResult[] = pillars.map((p) => {
    const qi = quizQuestions.findIndex((q) => q.pillar === p.key)
    const a = quizQuestions[qi].answers[answers[qi]]
    const score = a ? a.score : 0
    return { ...p, score, pct: Math.round((score / 3) * 100) }
  })
  const total = scored.reduce((s, p) => s + p.score, 0)
  const max = pillars.length * 3
  const b = bands.find((x) => total >= x.min) ?? bands[bands.length - 1]
  // Weakest three, ties broken by pillar order (water first, deliberately).
  const gaps = [...scored].sort((x, y) => x.score - y.score || pillars.findIndex((p) => p.key === x.key) - pillars.findIndex((p) => p.key === y.key)).slice(0, 3)
  return { band: b.band, headline: b.headline, total, max, pillars: scored, gaps }
}
